import { Group, Modal, Stack, TextInput } from '@mantine/core';
import type { EntityType } from '@repo/common/enums/entityType';
import { Button, Text } from '@repo/foundations';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { queryClient, type ISecodaEntity } from '../../api';
import {
	templatesQueryKeyFactory,
	useCreateTemplate,
} from '../../api/hooks/templates';
import type { SecodaEntity } from '../../lib/models/entity';
import { MultiTeamsSelector } from '../MultiTeamsSelector/MultiTeamsSelector';

interface TemplateModalProps {
	opened: boolean;
	onClose: () => void;
	type: EntityType;
	entity: SecodaEntity | ISecodaEntity;
}

export function TemplateModal({
	opened,
	onClose,
	type,
	entity,
}: TemplateModalProps) {
	const [teamAccessError, setTeamAccessError] = useState('');

	const { mutateAsync: createTemplate } = useCreateTemplate({
		options: {
			onSuccess: () => {
				onClose();
				queryClient.invalidateQueries({
					queryKey: templatesQueryKeyFactory.list(1),
				});
			},
		},
	});

	const formik = useFormik({
		initialValues: {
			name: '',
			usable_by_teams: [],
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Template name is required'),
			usable_by_teams: Yup.array().required('Team access is required'),
		}),
		onSubmit: (values) => {
			if (values.usable_by_teams.length === 0) {
				setTeamAccessError('Team access is required');
				return;
			}
			createTemplate({
				data: {
					...values,
					definition: entity.definition,
					entity_type_template: type,
				},
			});

			queryClient.invalidateQueries({
				queryKey: templatesQueryKeyFactory.list(1),
			});
		},
	});

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Convert to template"
			size="md"
		>
			<Stack spacing="md">
				<Text size="sm">
					Save this document as a template to apply across other {type}s.
				</Text>

				<form onSubmit={formik.handleSubmit}>
					<Stack spacing="md">
						<TextInput
							label="Template name"
							name="name"
							value={formik.values.name}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.name ? formik.errors.name : undefined}
						/>

						<MultiTeamsSelector
							label="Team access"
							value={formik.values.usable_by_teams}
							setValue={(val) => {
								setTeamAccessError('');
								formik.setFieldValue('usable_by_teams', val);
							}}
							required
							error={teamAccessError}
						/>

						<Group position="right" spacing="xs" mt="md">
							<Button type="button" onClick={onClose}>
								Cancel
							</Button>
							<Button variant="primary" type="submit">
								Create template
							</Button>
						</Group>
					</Stack>
				</form>
			</Stack>
		</Modal>
	);
}
